import type { User } from '@/payload-types'

/**
 * Client-safe check for a "2FA-pending" limited session (#3459 / #3566).
 *
 * A user who has 2FA enabled but not yet verified has authenticated (so they can complete
 * enrollment) but must NOT reach any tenant data until verification finishes — the server
 * denies such requests with 403 ("must complete 2FA setup"). The frontend shell must not
 * fire tenant-data fetches (companies, notifications, …) during this window, both to avoid
 * pointless 403s surfacing as console errors and because the data is intentionally withheld.
 *
 * This mirrors the server-side `isTwoFactorPending` in `@/utils/authUtils`, kept separate
 * because that module pulls in `payload` runtime code and is not client-bundle safe.
 *
 * To gate a tenant-data fetch, use `useTwoFactorBlocked` rather than this predicate directly:
 * impersonated sessions are 2FA-pending but *are* served by the server (#3891), and skipping
 * their fetches leaves the app blank.
 */
export const isTwoFactorPending = (user: User | null | undefined): boolean =>
  Boolean(user?.twoFactorAuth?.enabled && !user?.twoFactorAuth?.verified)
